import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { theme, pageTitleStyle, pageTitleTextStyle } from '../../theme';

const BASE = '/api';
function authFetch(url, opts = {}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { 'Content-Type': 'application/json', ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
}

const ORANGE = theme.orange;

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export default function DemandesOuverture() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [refusing, setRefusing] = useState(null); // {id} en cours de refus
  const [refusMotif, setRefusMotif] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await authFetch(`${BASE}/polices/demandes-ouverture`);
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Erreur'); setRows([]); return; }
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function valider(id) {
    setBusyId(id); setError('');
    try {
      const r = await authFetch(`${BASE}/polices/${id}/valider-ouverture`, { method: 'POST' });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Erreur'); return; }
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  }

  async function confirmerRefus() {
    if (!refusing) return;
    setBusyId(refusing.id); setError('');
    try {
      const r = await authFetch(`${BASE}/polices/${refusing.id}/refuser-ouverture`, {
        method: 'POST', body: JSON.stringify({ motif: refusMotif || null }),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Erreur'); return; }
      setRefusing(null); setRefusMotif('');
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  }

  return (
    <div>
      <div style={{ ...pageTitleStyle, marginBottom: 16 }}>
        <span style={pageTitleTextStyle}>{t('gf_requests.title')}</span>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: theme.textSecondary }}>
          {rows.length} {rows.length !== 1 ? t('gf_requests.count_plural') : t('gf_requests.count')}
        </span>
      </div>

      {error && <div style={{ color: theme.statut.decline.fg, marginBottom: 12, fontSize: 13 }}>⚠ {error}</div>}

      {loading ? (
        <div style={{ padding: 24, color: theme.textSecondary, fontSize: 14 }}>{t('common.loading')}</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: theme.textTertiary, fontSize: 14,
          background: theme.surface, borderRadius: theme.radiusLg, border: `0.5px solid ${theme.border}` }}>
          {t('gf_requests.empty')}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `0.5px solid ${theme.borderStrong}`,
                color: theme.textTertiary, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                {[t('gf_requests.col_client'), t('gf_requests.col_broker'), t('gf_requests.col_requester'),
                  t('gf_requests.col_date'), t('gf_requests.col_actions')].map((h, i) => (
                  <th key={i} style={{ textAlign: i === 4 ? 'right' : 'left', padding: '10px 12px', fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: `0.5px solid ${theme.border}` }}>
                  <td style={{ padding: '11px 12px' }}>
                    <button onClick={() => navigate(`/clients/${r.client_id}/detail`)}
                      style={{ background: 'none', border: 'none', padding: 0, color: theme.text, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                      {r.client_nom || '—'}
                    </button>
                    {r.client_pays && <div style={{ fontSize: 11, color: theme.textTertiary, marginTop: 1 }}>{r.client_pays}</div>}
                  </td>
                  <td style={{ padding: '11px 12px', color: theme.text }}>{r.courtier_nom || '—'}</td>
                  <td style={{ padding: '11px 12px', color: theme.text }}>
                    {r.demandeur_nom || '—'}
                    {r.demandeur_email && <div style={{ fontSize: 11, color: theme.textTertiary, marginTop: 1 }}>{r.demandeur_email}</div>}
                  </td>
                  <td style={{ padding: '11px 12px', whiteSpace: 'nowrap', fontSize: 12, color: theme.textSecondary }}>{fmtDate(r.demande_le)}</td>
                  <td style={{ padding: '11px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'inline-flex', gap: 8 }}>
                      <button onClick={() => valider(r.id)} disabled={busyId === r.id}
                        style={{ background: '#047857', color: '#fff', border: 'none', borderRadius: 7,
                          padding: '7px 14px', fontSize: 12.5, fontWeight: 600, cursor: busyId === r.id ? 'not-allowed' : 'pointer', opacity: busyId === r.id ? .6 : 1 }}>
                        {t('gf_requests.validate')}
                      </button>
                      <button onClick={() => { setRefusing(r); setRefusMotif(''); }} disabled={busyId === r.id}
                        style={{ background: '#fff', color: '#B91C1C', border: '1px solid #FCA5A5', borderRadius: 7,
                          padding: '7px 14px', fontSize: 12.5, fontWeight: 600, cursor: busyId === r.id ? 'not-allowed' : 'pointer' }}>
                        {t('gf_requests.refuse')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de refus (motif) */}
      {refusing && (
        <div onClick={() => busyId == null && setRefusing(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: 24, width: 'min(460px, calc(100% - 32px))' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: theme.text }}>{t('gf_requests.refuse_title')}</div>
            <div style={{ fontSize: 13, color: theme.textSecondary, marginBottom: 14 }}>
              {t('gf_requests.refuse_desc')} {refusing.client_nom ? `(${refusing.client_nom})` : ''}
            </div>
            <textarea value={refusMotif} onChange={e => setRefusMotif(e.target.value)}
              placeholder={t('gf_requests.refuse_motif_ph')} rows={3}
              style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${theme.borderStrong}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', marginBottom: 16 }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setRefusing(null)} disabled={busyId != null}
                style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${theme.border}`, background: '#fff', cursor: 'pointer' }}>
                {t('common.cancel')}
              </button>
              <button onClick={confirmerRefus} disabled={busyId != null}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#B91C1C', color: '#fff', fontWeight: 600, cursor: busyId != null ? 'not-allowed' : 'pointer', opacity: busyId != null ? .6 : 1 }}>
                {t('gf_requests.refuse_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
